import { useEffect, useState } from "react";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { trackInviteRequest } from "@/lib/siteTracking";
import { CheckCircle2, Loader2 } from "lucide-react";

const REQUEST_ACCESS_EVENT = "myelina:request-access:open";
const PUBLIC_ACCESS_REQUESTS_ENABLED =
  import.meta.env.VITE_ACCESS_REQUESTS_ENABLED === "true";

export function openRequestAccess(
  source?: string,
  prefill?: { name?: string; email?: string },
) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(REQUEST_ACCESS_EVENT, { detail: { source, prefill } }),
  );
}

const schema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "What should we call you?")
    .max(120, "That's a lot of letters, try a shorter version"),
  email: z
    .string()
    .trim()
    .email("Please double-check your email address")
    .max(255),
});

type FormState = { name: string; email: string };

const emptyState: FormState = { name: "", email: "" };

const RequestAccessDialog = () => {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState<string | undefined>();
  const [values, setValues] = useState<FormState>(emptyState);
  const [errors, setErrors] = useState<
    Partial<Record<keyof FormState, string>>
  >({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          source?: string;
          prefill?: { name?: string; email?: string };
        }>
      ).detail;
      setSource(detail?.source);
      setSubmitted(false);
      setErrors({});
      setValues({
        name: detail?.prefill?.name ?? "",
        email: detail?.prefill?.email ?? "",
      });
      setOpen(true);
    };
    window.addEventListener(REQUEST_ACCESS_EVENT, handler);
    return () => window.removeEventListener(REQUEST_ACCESS_EVENT, handler);
  }, []);

  const update = <K extends keyof FormState>(
    key: K,
    value: FormState[K],
  ) => {
    setValues((current) => ({ ...current, [key]: value }));
    if (errors[key]) {
      setErrors((current) => ({ ...current, [key]: undefined }));
    }
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Defense in depth: the form is not rendered while collection is held, but
    // a programmatic submit must still fail closed.
    if (!PUBLIC_ACCESS_REQUESTS_ENABLED) {
      toast.error("Public access requests are currently paused.");
      return;
    }

    const parsed = schema.safeParse(values);
    if (!parsed.success) {
      const fieldErrors: Partial<Record<keyof FormState, string>> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof FormState;
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.from("access_requests").insert({
        name: parsed.data.name,
        email: parsed.data.email,
        source: source || "landing",
      });

      if (error && error.code !== "23505") throw error;

      trackInviteRequest(error?.code === "23505" ? "duplicate" : "submitted", {
        source,
      });
      setSubmitted(true);
      setValues(emptyState);
    } catch (error) {
      console.error("access request failed", error);
      toast.error("We couldn't send that just now. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        {!PUBLIC_ACCESS_REQUESTS_ENABLED ? (
          <div className="py-4 space-y-5">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">
                Public access requests are paused
              </DialogTitle>
              <DialogDescription>
                We are verifying the current privacy notice, invitation controls,
                and production data flow before collecting new requests. This
                dialog does not collect or transmit your information. Do not send
                health or medical information by email or through public forms.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="premium" onClick={() => setOpen(false)}>
                Close
              </Button>
              <Button variant="outline" asChild>
                <a href="mailto:hello@myelinahealth.com?subject=Myelina%20pilot%20information">
                  Request written information
                </a>
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Email is provided only as a contact channel. Do not include health,
              medical, or other sensitive information.
            </p>
          </div>
        ) : submitted ? (
          <div className="py-6 text-center space-y-3">
            <CheckCircle2
              className="w-10 h-10 mx-auto text-primary"
              aria-hidden="true"
            />
            <DialogTitle className="font-display text-2xl">
              Request received
            </DialogTitle>
            <DialogDescription className="text-sm">
              This is not an invitation, enrollment, or guarantee of access. If
              pilot capacity and current eligibility requirements can be
              confirmed, we may contact you at the email you supplied.
            </DialogDescription>
            <div className="flex flex-col sm:flex-row gap-2 justify-center mt-4">
              <Button variant="premium" onClick={() => setOpen(false)}>
                Close
              </Button>
              <Button variant="outline" asChild>
                <a href="/support">Contact support</a>
              </Button>
            </div>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">
                Request limited pilot access
              </DialogTitle>
              <DialogDescription>
                Provide only your name and email. Do not include health or
                medical information. Submitting a request does not guarantee an
                invitation or enrollment.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={onSubmit} className="space-y-4 mt-2" noValidate>
              <div className="space-y-1.5">
                <Label htmlFor="ra-name">Your name</Label>
                <Input
                  id="ra-name"
                  value={values.name}
                  onChange={(event) => update("name", event.target.value)}
                  autoComplete="name"
                  maxLength={120}
                  placeholder="First name is fine"
                  required
                />
                {errors.name && (
                  <p className="text-xs text-destructive">{errors.name}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ra-email">Email</Label>
                <Input
                  id="ra-email"
                  type="email"
                  value={values.email}
                  onChange={(event) => update("email", event.target.value)}
                  autoComplete="email"
                  maxLength={255}
                  placeholder="you@example.com"
                  required
                />
                <p className="text-[11px] text-muted-foreground">
                  This address is used to review and respond to the request.
                </p>
                {errors.email && (
                  <p className="text-xs text-destructive">{errors.email}</p>
                )}
              </div>
              <Button
                type="submit"
                variant="premium"
                className="w-full min-h-12"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2
                      className="w-4 h-4 mr-2 animate-spin"
                      aria-hidden="true"
                    />
                    Sending your request…
                  </>
                ) : (
                  "Send access request"
                )}
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                Review the{" "}
                <a
                  href="/privacy"
                  className="underline underline-offset-4 text-foreground"
                >
                  Privacy Policy
                </a>{" "}
                before submitting. Do not use this form for health or medical
                information.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RequestAccessDialog;
